import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Checkbox from 'components/Checkbox';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { postAppointmentForm, setFormData } from 'modules/forms/actions/form.actions';
import { acuteSinusitisRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { getFormResultHTML } from 'modules/forms/helpers/formResult.helper';
import FormResult from './FormResult';

const AdditionalAssessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      additionalSymptoms: stateForm.additionalSymptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, setValue, handleSubmit } = methods;
  const additionalSymptoms = watch('additionalSymptoms') || [];

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(setFormData(values));
    const status = stateForm.status || EAppointmentFormStatus.COMPLETED;
    dispatch(
      postAppointmentForm({
        form: {
          appointmentUuid: appointment?.uuid,
          status: values?.additionalSymptoms?.includes('6')
            ? EAppointmentFormStatus.NEEDS_REVIEW
            : status,
          response: getFormResultHTML({ ...stateForm, ...values }, FormResult),
        },
        route: acuteSinusitisRoutes.confirmation,
      }),
    );
  };

  const onChangeValue = (name: string, e: ChangeEvent<HTMLInputElement>) => {
    setValue(
      'additionalSymptoms',
      e.target.checked
        ? [...additionalSymptoms, name].filter(item => item !== '6')
        : additionalSymptoms.filter(item => item !== name),
    );
  };

  return (
    <Page>
      <PageHeader step={7} maxSteps={7} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Which of the following symptoms is the patient experiencing (select all that apply):"
              >
                <Controller
                  name="additionalSymptoms"
                  control={control}
                  render={() => (
                    <FormGroup>
                      <Checkbox
                        id="1"
                        name="1"
                        checked={additionalSymptoms?.includes('1')}
                        onChange={e => onChangeValue('1', e)}
                        label="Runny Nose: Dripping or stuffiness in the nose, where the drip might be at the front or back"
                      />
                      <Checkbox
                        id="2"
                        name="2"
                        checked={additionalSymptoms?.includes('2')}
                        onChange={e => onChangeValue('2', e)}
                        label="Blocked Nose: Feeling like the nose is stuffy or blocked"
                      />
                      <Checkbox
                        id="3"
                        name="3"
                        checked={additionalSymptoms?.includes('3')}
                        onChange={e => onChangeValue('3', e)}
                        label="Face Hurts or Feels Heavy: Pain or pressure on your face, or a headache"
                      />
                      <Checkbox
                        id="4"
                        name="4"
                        checked={additionalSymptoms?.includes('4')}
                        onChange={e => onChangeValue('4', e)}
                        label="Cough (children only): Coughing during the day or at night (relevant for children only)"
                      />
                      <Checkbox
                        id="5"
                        name="5"
                        checked={additionalSymptoms?.includes('5')}
                        onChange={e => onChangeValue('5', e)}
                        label="Can't Smell Well (adults only): Not being able to smell things as well as usual (relevant for adults only)"
                      />
                      <Checkbox
                        id="6"
                        name="6"
                        checked={additionalSymptoms?.includes('6')}
                        onChange={e =>
                          setValue('additionalSymptoms', e.target.checked ? ['6'] : [])
                        }
                        label="None of the Above"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={!additionalSymptoms?.length}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default AdditionalAssessment;
